import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { collection, getDocs, query, updateDoc, doc } from "firebase/firestore"
import { db, addPrefix } from '../firebase'
import { Nav, Navbar } from 'reactstrap'
import Select from 'react-select'
import agentPlaceholder from "../assets/agent.png"
import adminPlaceholder from '../assets/placeholder.png'
import './Header.css'
import ErrorModal from '../Components/ErrorModal'
import VGMLogo from "../assets/VGMLogo.png"
import TenantBranchSelectionModal from '../Checkin/TenantBranchSelectionModal'
import { groupedHeaderDropdownCustomStyling, getHeaderDropdownCustomStyling } from '../constants'
import { toTitleCase } from '../functions'

function Header({ currentUser, allLocations, activeDrivers, allLanguages, handleLogout, handleUserUpdate, userUpdateError, handleCloseErrorModel, setCurrentUser = null }) {
    const [branch, setBranch] = useState("")
    const [allBranches, setAllBranches] = useState([])
    const [branchOptions, setBranchOptions] = useState([])
    const [selectedBranchOption, setSelectedBranchOption] = useState(null)
    const pathName = window.location.pathname
    const [error, setError] = useState("")
    const [openModal, setOpenModal] = useState("")

    const isUserBusy = async (agentId) => {
        return activeDrivers.filter(driver => driver.status === "assigned" && driver.agent === Number(agentId)).length > 0
    }

    useEffect(() => {
        loadAllBranches()
        // Get branch from hash fragment
        const hash = window.location.hash.replace('#', '')
        if (hash)
            setBranch(hash)

        if (!hash && currentUser && currentUser.selectedBranch) {
            setBranch(currentUser.selectedBranch)
            window.location.hash = currentUser.selectedBranch
        } else if (!hash) {
            setOpenModal("branchSelection")
        }
    }, [currentUser])

    const handleRoleSwitch = async (isAgent, status) => {
        try {
            if (await isUserBusy(currentUser.pk) && !isAgent) {
                setError("Please complete your assigned task in order to switch to admin.")
                return
            }
            if (currentUser.pk) {
                await updateDoc(doc(db, addPrefix("users"), String(currentUser.pk)), {
                    status: status,
                    isAgent: isAgent,
                })
            }
            window.location.reload(false)
        } catch (err) {
            setError(`Error: Unable to login as ${isAgent ? "agent" : "admin"}!`)
        }
    }

    const loadAllBranches = async () => {
        const querySnapshot = await getDocs(query(collection(db, addPrefix("branches"))))
        const branches = querySnapshot.docs.map((doc) => ({ ...doc.data(), firebaseId: doc.id }))
        setAllBranches(branches)
        // Extract unique tenants from branches and sort them
        let tenants = [...new Set(branches.map(branch => branch.tenant))].filter(Boolean).sort((a, b) => a.localeCompare(b))

        // filter out branches of other tenant for checkin path
        if (window.location.pathname.includes("checkin")) {
            tenants = tenants.filter(tenant => tenant.toLowerCase() !== "other")
        }
        // Group branches by tenant
        const grouped = {}
        tenants.forEach(tenantName => {
            grouped[tenantName] = branches.filter(branch => branch.tenant === tenantName)
        })

        // Create options for react-select with groups (tenants and branches sorted by name)
        const options = tenants.map(tenantName => ({
            label: tenantName,
            options: grouped[tenantName]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(branchItem => ({
                    value: `${tenantName}-${branchItem.customId}`,
                    label: branchItem.name,
                    tenant: tenantName,
                    branchId: branchItem.customId,
                    branchData: branchItem
                }))
        }))
        setBranchOptions(options)

        // Set current selected option from user's selectedBranch or hash
        let currentBranch = null
        const hash = window.location.hash.replace('#', '')

        if (currentUser && currentUser.selectedBranch && !hash) {
            // Use the stored selectedBranch from user document
            currentBranch = branches.find(item => item.customId === currentUser.selectedBranch)
            if (currentBranch) {
                setBranch(currentBranch.customId)
            }
        } else if (hash) {
            // Use branch from hash
            currentBranch = branches.find(item => item.customId === hash)
            if (currentBranch) {
                setBranch(currentBranch.customId)
            }
        }

        if (currentBranch) {
            const currentOption = {
                value: `${currentBranch.tenant}-${currentBranch.customId}`,
                label: currentBranch.name,
                tenant: currentBranch.tenant,
                branchId: currentBranch.customId,
                branchData: currentBranch
            }
            setSelectedBranchOption(currentOption)
        }
    }

    const setParam = (branch) => {
        window.location.hash = branch
        window.location.reload()
    }

    const handleBranchChange = async (selectedBranchOption) => {
        if (currentUser && await isUserBusy(currentUser.pk)) {
            setError("Please complete the assigned task to perform this action")
            return
        }
        if (selectedBranchOption && currentUser && currentUser.pk) {
            try {
                await updateDoc(doc(db, addPrefix("users"), String(currentUser.pk)), {
                    selectedBranch: selectedBranchOption.branchId,
                })

                if (setCurrentUser) {
                    setCurrentUser({
                        ...currentUser,
                        selectedBranch: selectedBranchOption.branchId,
                    })
                }

                setSelectedBranchOption(selectedBranchOption)
                setParam(selectedBranchOption.branchId)
            } catch (err) {
                setError("Error: Unable to save branch selection!")
            }
        } else if (selectedBranchOption) {
            setSelectedBranchOption(selectedBranchOption)
            setParam(selectedBranchOption.branchId)
        }
    }

    useEffect(() => {
        if (!allBranches || allBranches.length === 0) {
            setOpenModal("")
            return true
        } else if (window.location.pathname === "/admin/login") {
            setOpenModal("")
        }
        else if (branch && allBranches.find(item => item.customId.toLowerCase() === branch.toLowerCase())) {
            setOpenModal("")
        } else {
            setOpenModal("branchSelection")
        }
    }, [allBranches, branch])

    useEffect(() => {
        if (!allBranches || allBranches.length === 0) {
            setOpenModal("")
            return
        }

        // Find the branch in the loaded branches
        const selectedBranch = branch && allBranches.find(item => item.customId.toLowerCase() === branch.toLowerCase())

        // For checkin, branch must not be from 'Other' tenant
        const isValidQueueForCheckin = selectedBranch && (!window.location.pathname.includes("checkin") || selectedBranch.tenant.toLowerCase() !== "other")

        if (window.location.pathname === "/admin/login") {
            setOpenModal("")
        } else if (!branch || !selectedBranch || !isValidQueueForCheckin) {
            // If branch is missing, not found, or not valid for checkin, clear hash and show modal
            window.location.hash = ""
            setBranch("")
            setOpenModal("branchSelection")
        } else {
            setOpenModal("")
        }
    }, [allBranches, branch])

    // User dropdown options
    const userOptions = []
    if (currentUser && currentUser.groupNames && currentUser.groupNames.includes("VMS-Admin") && currentUser.isAgent) {
        userOptions.push(
            {
                value: 'toggle-status',
                label: currentUser.status === "active" ? "Inactive" : "Active"
            },
            {
                value: 'switch-admin',
                label: "Switch To Admin"
            }
        )
    } else if (currentUser) {
        userOptions.push({
            value: 'switch-agent',
            label: "Switch To Agent"
        })
    }
    userOptions.push({ value: 'logout', label: "Logout" })

    const handleUserSelect = async (option) => {
        if (!option) return
        if (option.value === 'toggle-status') {
            if (currentUser.status === "active") {
                if (await isUserBusy(currentUser.pk)) {
                    setError("Please complete your assigned ticket before changing your status to inactive.")
                    return
                }
            }
            handleUserUpdate("status", currentUser.status === "active" ? "inactive" : "active")
        } else if (option.value === 'switch-admin') {
            handleRoleSwitch(false, "inactive")
        } else if (option.value === 'switch-agent') {
            handleRoleSwitch(true, "active")
        } else if (option.value === 'logout') {
            handleLogout()
        }
    }

    // Location dropdown options
    const locationOptions = allLocations?.filter(loc => loc.customBranchId === branch).map(loc => ({ value: loc.name, label: loc.name })) || []
    const handleLocationSelect = (option) => {
        if (option) handleUserUpdate("location", option.value)
    }

    // Language dropdown options
    const languageOptions = allLanguages?.map(lang => ({ value: lang.name, label: lang.name })) || []
    const handleLanguageSelect = (option) => {
        if (option) handleUserUpdate("language", option.value)
    }

    return (
        <Navbar light expand="xs" className="vms-header">
            <Nav className="mr-auto d-flex flex-row justify-content-between" navbar>
                {openModal === "branchSelection" && <TenantBranchSelectionModal open={openModal === "branchSelection"} handleClose={() => setOpenModal(" ")} handleSetParam={(branch) => setParam(branch)} currentUser={currentUser} />}
                <div className="text-center align-self-center">
                    <Link className="logo" to={pathName.includes("admin") ? `/admin#${branch}` : "/"}>
                        <img src={VGMLogo} width={60} alt="VGM Logo" />
                    </Link>
                    {!pathName.includes("admin") && (
                        <>
                            <Link className={"navItem " + (pathName.includes("checkin") ? "active" : "inactive")} to={`/checkin#${branch}`}>CHECKIN</Link> &nbsp;
                            <Link className={"navItem " + (pathName.includes("queue") ? "active" : "inactive")} to={`/queue#${branch}`}>QUEUE</Link>&nbsp;
                            <Link className={"navItem"} to={`/admin#${branch}`}>ADMIN</Link>&nbsp;
                        </>
                    )}
                </div>

                {error && <ErrorModal open={!!error} handleClose={() => setError("")} message={error} />}
                <div className={`nav-elements`}>
                    <ul>
                        {currentUser && <>
                            <li style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span className={"vms-avatar " + (currentUser.status === "active" ? "border-green" : "")} style={{ width: "30px" }}>
                                    <img src={currentUser.isAgent ? agentPlaceholder : adminPlaceholder} className="w-100" alt="" />
                                </span>
                                <Select
                                    className="user-select"
                                    classNamePrefix="user-select"
                                    value={null}
                                    onChange={handleUserSelect}
                                    options={userOptions}
                                    isSearchable={false}
                                    placeholder={currentUser?.username ? toTitleCase(currentUser.username) : "--"}
                                    styles={getHeaderDropdownCustomStyling("130px")}
                                />
                            </li>
                            {userUpdateError && userUpdateError !== "" && <ErrorModal open={userUpdateError} handleClose={handleCloseErrorModel} message={userUpdateError} />}
                            {currentUser.isAgent &&
                                <>
                                    <li>
                                        <Select
                                            className="location-select"
                                            classNamePrefix="location-select"
                                            placeholder={currentUser.location ? currentUser.location : <span style={{ color: 'red' }}>No Location Selected</span>}
                                            options={locationOptions}
                                            value={locationOptions.find(opt => opt.value === currentUser.location) || null}
                                            onChange={handleLocationSelect}
                                            styles={currentUser.location ? getHeaderDropdownCustomStyling("150px") : {
                                                ...getHeaderDropdownCustomStyling("150px"),
                                                placeholder: (provided) => ({
                                                    ...provided,
                                                    color: 'red',
                                                })
                                            }}
                                            noOptionsMessage={() => <span className="text-danger">No Location</span>}
                                        />
                                    </li>
                                    <li>
                                        <Select
                                            value={languageOptions.find(opt => opt.value === currentUser.language) || null}
                                            onChange={handleLanguageSelect}
                                            options={languageOptions}
                                            placeholder={currentUser.language ? currentUser.language : "No Language Selected"}
                                            isSearchable={false}
                                            isClearable={false}
                                            styles={getHeaderDropdownCustomStyling("100px")}
                                            className="language-select"
                                            classNamePrefix="language-select"
                                            menuPlacement="bottom"
                                            components={{ IndicatorSeparator: () => null }}
                                            noOptionsMessage={() => <span>No Language</span>}
                                        />
                                    </li>
                                </>}
                        </>}
                        <li className="branch-select-container">
                            {window.location.pathname !== "/admin/login" &&
                                <Select
                                    value={selectedBranchOption}
                                    onChange={handleBranchChange}
                                    options={branchOptions}
                                    placeholder="Select Queue"
                                    isSearchable={true}
                                    isClearable={false}
                                    styles={groupedHeaderDropdownCustomStyling}
                                    className="branch-select"
                                    classNamePrefix="branch-select"
                                />
                            }
                        </li>
                    </ul>
                </div>
            </Nav>
        </Navbar>
    )
}

export default Header
